/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

function DaftarLayanan() {
  const [layananList, setLayananList] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const [userStatus, setUserStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bidangList, setBidangList] = useState([]);
  const [editingLayananData, setEditingLayananData] = useState(null);
  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    layanan: "",
    keterangan: "",
    status: "draft",
    id_bidang: null,
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setUserStatus(user.status);
    } else {
      logoutUser(navigate);
    }
  }, []);

  const fetchLayanan = async () => {
    if (!dinasId) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}survey-kepuasan/layanan-publik?dinasId=${dinasId}`
      );
      setLayananList(response.data || []);
    } catch (error) {
      console.error("Error fetching layanan:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data layanan publik",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBidang = async () => {
    if (!dinasId) return;

    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}survey-kepuasan/bidang/${dinasId}`
      );
      setBidangList(response.data || []);
    } catch (error) {
      console.error("Error fetching bidang:", error);
    }
  };

  useEffect(() => {
    if (dinasId) {
      fetchLayanan();
      fetchBidang();
    }
  }, [dinasId]);

  // Update formData.id_bidang setelah bidangList ter-load saat edit
  useEffect(() => {
    if (
      openDialog &&
      editingId &&
      editingLayananData &&
      bidangList.length > 0
    ) {
      const bidangId =
        editingLayananData.id_bidang || editingLayananData.bidang_id || null;
      if (bidangId) {
        // Konversi ke number untuk memastikan matching
        const bidangIdNum = Number(bidangId);
        const foundBidang = bidangList.find(
          (b) => Number(b.bidang_id) === bidangIdNum
        );
        if (foundBidang) {
          setFormData((prev) => ({
            ...prev,
            id_bidang: bidangIdNum,
          }));
        }
      }
    }
  }, [openDialog, editingId, editingLayananData, bidangList]);

  const handleOpenDialog = async () => {
    // Pastikan bidangList sudah ter-load
    if (bidangList.length === 0 && dinasId) {
      await fetchBidang();
    }

    setEditingId(null);
    setEditingLayananData(null);
    setFormData({
      layanan: "",
      keterangan: "",
      status: "draft",
      cek_ip_address: "tidak",
      id_bidang: null,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setEditingLayananData(null);
    setFormData({
      layanan: "",
      keterangan: "",
      status: "draft",
      cek_ip_address: "tidak",
      id_bidang: null,
    });
    setSubmitting(false);
  };

  const handleEdit = async (layanan) => {
    // Simpan data layanan untuk digunakan di useEffect
    setEditingLayananData(layanan);

    // Pastikan bidangList sudah ter-load
    if (bidangList.length === 0 && dinasId) {
      await fetchBidang();
    }

    setEditingId(layanan.id);
    // Gunakan id_bidang atau bidang_id (dari join) sebagai fallback
    // Konversi ke number untuk konsistensi
    const bidangId = layanan.id_bidang || layanan.bidang_id || null;
    const bidangIdNum = bidangId ? Number(bidangId) : null;

    setFormData({
      layanan: layanan.layanan || "",
      keterangan: layanan.keterangan || "",
      status: layanan.status || "draft",
      cek_ip_address: layanan.cek_ip_address || "tidak",
      id_bidang: bidangIdNum,
    });
    setOpenDialog(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStatusChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      status: newValue || "draft",
    }));
  };

  const handleCekIPAddressChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      cek_ip_address: newValue || "tidak",
    }));
  };

  const statusOptions = ["draft", "aktif", "tutup"];
  const cekIPAddressOptions = ["ya", "tidak"];

  const handleSubmit = async () => {
    if (submitting) return; // Prevent double submission

    if (!formData.layanan) {
      swal({
        title: "Error",
        text: "Nama layanan harus diisi",
        icon: "error",
      });
      return;
    }

    // Validasi wajib bidang untuk create (tambah baru)
    if (!editingId && !formData.id_bidang) {
      swal({
        title: "Error",
        text: "Bidang harus diisi",
        icon: "error",
      });
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        // Update
        await axios.put(
          `${API_ESA_OFFICE}survey-kepuasan/layanan-publik/${editingId}`,
          {
            id_dinas: dinasId,
            id_bidang: formData.id_bidang || null,
            layanan: formData.layanan,
            keterangan: formData.keterangan,
            status: formData.status,
            cek_ip_address: formData.cek_ip_address || "tidak",
          }
        );
        swal({
          title: "Berhasil!",
          text: "Layanan publik berhasil diupdate",
          icon: "success",
          button: "OK",
        });
      } else {
        // Create
        await axios.post(`${API_ESA_OFFICE}survey-kepuasan/layanan-publik`, {
          id_dinas: dinasId,
          id_bidang: formData.id_bidang,
          layanan: formData.layanan,
          keterangan: formData.keterangan,
          status: formData.status,
          cek_ip_address: formData.cek_ip_address || "tidak",
        });
        swal({
          title: "Berhasil!",
          text: "Layanan publik berhasil ditambahkan",
          icon: "success",
          button: "OK",
        });
      }

      handleCloseDialog();
      fetchLayanan();
    } catch (error) {
      console.error("Error saving layanan:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal menyimpan layanan publik",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Data layanan publik akan dihapus secara permanen!",
      icon: "warning",
      buttons: ["Batal", "Ya, Hapus!"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axios.delete(
            `${API_ESA_OFFICE}survey-kepuasan/layanan-publik/${id}`
          );
          swal({
            title: "Berhasil!",
            text: "Layanan publik berhasil dihapus",
            icon: "success",
            button: "OK",
          });
          fetchLayanan();
        } catch (error) {
          swal({
            title: "Error!",
            text:
              error.response?.data?.message ||
              "Terjadi kesalahan saat menghapus layanan publik",
            icon: "error",
            button: "OK",
          });
        }
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "aktif":
        return "success";
      case "draft":
        return "warning";
      case "tutup":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "aktif":
        return "Aktif";
      case "draft":
        return "Draft";
      case "tutup":
        return "Tutup";
      default:
        return status;
    }
  };

  const getCekIPAddressLabel = (value) => {
    if (!value) return "Tidak";
    return value.toLowerCase() === "ya" ? "Ya" : "Tidak";
  };

  const getCekIPAddressColor = (value) => {
    if (!value) return "default";
    return value.toLowerCase() === "ya" ? "success" : "default";
  };

  // Hanya admin atau super admin yang bisa melakukan CRUD
  const isAdmin = userStatus === "admin" || userStatus === "super admin";

  const columns = [
    { Header: "No", accessor: "no", width: "5%" },
    { Header: "Nama Layanan", accessor: "layanan", width: "20%" },
    { Header: "Keterangan", accessor: "keterangan", width: "25%" },
    {
      Header: "Bidang",
      accessor: "nama_bidang",
      width: "15%",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            maxWidth: "200px",
            py: 1,
          }}
        >
          <MDTypography variant="caption" color="text">
            {value || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          label={getStatusLabel(value)}
          color={getStatusColor(value)}
          size="small"
          variant="filled"
        />
      ),
    },
    {
      Header: "Cek IP Address",
      accessor: "cek_ip_address",
      width: "12%",
      Cell: ({ value }) => (
        <Chip
          label={getCekIPAddressLabel(value)}
          color={getCekIPAddressColor(value)}
          size="small"
          variant="filled"
        />
      ),
    },
    {
      Header: "Link",
      accessor: "link",
      width: "15%",
      Cell: ({ value, row }) => (
        <MDBox>
          {value ? (
            <MDBox display="flex" alignItems="center" gap={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  window.open(value, "_blank");
                }}
                sx={{
                  color: "#000000",
                  borderColor: "#000000",
                  "&:hover": {
                    borderColor: "#000000",
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                Open Link
              </Button>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          )}
        </MDBox>
      ),
    },
    {
      Header: "QR Code",
      accessor: "qrcode",
      width: "10%",
      Cell: ({ row }) => (
        <MDBox>
          {row.original.qr_code ? (
            <img
              src={`https://esa-office.rotendaokab.go.id${
                row.original.qr_code.startsWith("/")
                  ? row.original.qr_code
                  : "/" + row.original.qr_code
              }`}
              alt="QR Code"
              style={{ width: 50, height: 50, cursor: "pointer" }}
              onClick={() => {
                // Open QR code in new window
                const qrPath = row.original.qr_code.startsWith("/")
                  ? row.original.qr_code
                  : "/" + row.original.qr_code;
                window.open(
                  `https://esa-office.rotendaokab.go.id${qrPath}`,
                  "_blank"
                );
              }}
            />
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          )}
        </MDBox>
      ),
    },
    ...(isAdmin
      ? [
          {
            Header: "Aksi",
            accessor: "aksi",
            width: "10%",
            Cell: ({ row }) => (
              <MDBox display="flex" gap={1}>
                <IconButton
                  color="info"
                  size="small"
                  onClick={() => handleEdit(row.original)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => handleDelete(row.original.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </MDBox>
            ),
          },
        ]
      : []),
  ];

  const rows = layananList.map((layanan, index) => ({
    id: layanan.id,
    no: index + 1,
    layanan: layanan.layanan,
    keterangan: layanan.keterangan || "-",
    nama_bidang: layanan.nama_bidang || "-",
    status: layanan.status,
    cek_ip_address: layanan.cek_ip_address,
    link: layanan.link,
    qr_code: layanan.qr_code,
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Daftar Layanan Publik
                </MDTypography>
                {isAdmin && (
                  <Button
                    variant="contained"
                    color="white"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                  >
                    Tambah Layanan
                  </Button>
                )}
              </MDBox>
              <MDBox pt={4} pb={3}>
                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data layanan...
                    </MDTypography>
                  </MDBox>
                ) : isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    {layananList.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data layanan publik
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          {isAdmin
                            ? "Klik tombol 'Tambah Layanan' untuk menambahkan layanan baru"
                            : "Belum ada layanan publik yang terdaftar"}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      layananList.map((layanan, index) => (
                        <Card key={layanan.id}>
                          <CardContent>
                            <MDBox>
                              <MDTypography variant="h6" fontWeight="bold">
                                {layanan.layanan}
                              </MDTypography>
                              <MDBox mt={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Keterangan:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {layanan.keterangan || "-"}
                                </MDTypography>
                              </MDBox>
                              {layanan.nama_bidang && (
                                <MDBox mt={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Bidang:
                                  </MDTypography>
                                  <MDTypography variant="body2" color="text">
                                    {layanan.nama_bidang}
                                  </MDTypography>
                                </MDBox>
                              )}
                              <MDBox
                                mt={1}
                                display="flex"
                                gap={1}
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Status:
                                </MDTypography>
                                <Chip
                                  label={getStatusLabel(layanan.status)}
                                  color={getStatusColor(layanan.status)}
                                  size="small"
                                  variant="filled"
                                />
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Cek IP:
                                </MDTypography>
                                <Chip
                                  label={getCekIPAddressLabel(
                                    layanan.cek_ip_address
                                  )}
                                  color={getCekIPAddressColor(
                                    layanan.cek_ip_address
                                  )}
                                  size="small"
                                  variant="filled"
                                />
                              </MDBox>
                              {layanan.link && (
                                <MDBox mt={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Link Survey:
                                  </MDTypography>
                                  <MDBox
                                    display="flex"
                                    gap={1}
                                    alignItems="center"
                                    mt={0.5}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      color="info"
                                      sx={{
                                        wordBreak: "break-all",
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      {layanan.link}
                                    </MDTypography>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="info"
                                      onClick={() => {
                                        navigator.clipboard.writeText(
                                          layanan.link
                                        );
                                        swal({
                                          title: "Berhasil!",
                                          text: "Link berhasil disalin",
                                          icon: "success",
                                          button: "OK",
                                        });
                                      }}
                                    >
                                      Salin
                                    </Button>
                                  </MDBox>
                                </MDBox>
                              )}
                              {layanan.qr_code && (
                                <MDBox mt={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    QR Code:
                                  </MDTypography>
                                  <MDBox mt={0.5}>
                                    <img
                                      src={`https://esa-office.rotendaokab.go.id${
                                        layanan.qr_code.startsWith("/")
                                          ? layanan.qr_code
                                          : "/" + layanan.qr_code
                                      }`}
                                      alt="QR Code"
                                      style={{
                                        width: 100,
                                        height: 100,
                                        cursor: "pointer",
                                      }}
                                      onClick={() => {
                                        const qrPath =
                                          layanan.qr_code.startsWith("/")
                                            ? layanan.qr_code
                                            : "/" + layanan.qr_code;
                                        window.open(
                                          `https://esa-office.rotendaokab.go.id${qrPath}`,
                                          "_blank"
                                        );
                                      }}
                                    />
                                  </MDBox>
                                </MDBox>
                              )}
                              {isAdmin && (
                                <MDBox mt={2} display="flex" gap={1}>
                                  <Button
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleEdit(layanan)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleDelete(layanan.id)}
                                  >
                                    Hapus
                                  </Button>
                                </MDBox>
                              )}
                            </MDBox>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 15, 20, 25],
                    }}
                    showTotalEntries={true}
                    isSorted={true}
                    noEndBorder
                    loading={loading}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog Form Tambah/Edit Layanan */}
      {isAdmin && (
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6">
                {editingId
                  ? "Edit Layanan Publik"
                  : "Tambah Layanan Publik Baru"}
              </MDTypography>
              <IconButton onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox pt={2} pb={3} px={3}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDInput
                    label="Nama Layanan"
                    name="layanan"
                    value={formData.layanan}
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDInput
                    label="Keterangan"
                    name="keterangan"
                    value={formData.keterangan}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    key={`bidang-${formData.id_bidang}-${bidangList.length}`}
                    options={bidangList}
                    value={
                      formData.id_bidang && bidangList.length > 0
                        ? bidangList.find(
                            (b) =>
                              Number(b.bidang_id) === Number(formData.id_bidang)
                          ) || null
                        : null
                    }
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        id_bidang: newValue ? Number(newValue.bidang_id) : null,
                      }));
                    }}
                    getOptionLabel={(option) => option.nama_bidang || ""}
                    isOptionEqualToValue={(option, value) =>
                      Number(option.bidang_id) === Number(value?.bidang_id)
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label={editingId ? "Bidang (Opsional)" : "Bidang *"}
                        variant="outlined"
                        fullWidth
                        required={!editingId}
                        error={!editingId && !formData.id_bidang}
                        helperText={
                          !editingId && !formData.id_bidang
                            ? "Bidang wajib diisi"
                            : ""
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={statusOptions}
                    value={formData.status}
                    onChange={handleStatusChange}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Status"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    getOptionLabel={(option) => {
                      const labels = {
                        draft: "Draft",
                        aktif: "Aktif",
                        tutup: "Tutup",
                      };
                      return labels[option] || option;
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={cekIPAddressOptions}
                    value={formData.cek_ip_address || "tidak"}
                    onChange={handleCekIPAddressChange}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Cek IP Address"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    getOptionLabel={(option) => {
                      const labels = {
                        ya: "Ya",
                        tidak: "Tidak",
                      };
                      return labels[option] || option;
                    }}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Batal
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="info"
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {submitting ? "Menyimpan..." : editingId ? "Update" : "Simpan"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default DaftarLayanan;
